/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { normalizeNumber } from "helpers/constants";
import { contractEntitiesUrl } from "@Services/dataManager";
import { forwardRef, useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { GrClose } from "react-icons/gr";
import axiosWrapper from "@Services/axiosWrapper";
import { getTaskId } from "@CubeComponents/constants";
import { useTranslation } from "@i18n";
import Shimmer from "@Components/Queries/Shimmer";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { typedColumns, type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
import type { ToolbarItem, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";
import { convertDate } from "@Services/luxonHelper";
import { Formatters } from "slickgrid-react";
import { RootState } from "@Reducers/index";
import type { GetContractLinkedEntitiesResponse } from "@ServerTypes/contracts/contracts.schema";

/**
 * Row type for contract task data. The list endpoint's response is
 * `result: z.array(z.any())` (`getContractLinkedEntitiesResponse`), so there is no server row type to
 * import and only the fields this grid reads are named. SERVER SCHEMA GAP — reported.
 */
export interface ContractTaskRow extends GridRowBase {
  scheduleCounter?: string;
  title?: string;
  startDate?: number;
  endDate?: number;
  plannedStartDate?: number;
  plannedEndDate?: number;
  actualStartDate?: number;
  actualEndDate?: number;
  progress?: number;
  createdDate?: number;
  hasChild?: boolean;
  parentId?: string;
  originalTaskId?: string;
  isAlreadyAddedInThisForm?: boolean;
  isAlreadyAddedInThisContract?: boolean;
}

/** The slice of the SlickGrid1 imperative handle this grid drives. */
interface ContractTasksGridHandle {
  dataset: ContractTaskRow[];
  slickGrid: { getSelectedRows: () => number[]; getDataItem: (row: number) => ContractTaskRow };
  treeDataService: { toggleTreeDataCollapse: (collapse: boolean) => void };
}

/** Per-caller overrides for where the grid reads from and what sits in its toolbar. */
interface ContractTasksConfigParams {
  dataSourcePayload?: unknown;
  dataSourceUrl?: string;
  idMapping?: string;
  toolbar?: ToolbarItem[] | null;
}

interface ContractTasksProps {
  contractId?: string;
  formId?: string;
  setAttachBtnState?: (state: boolean) => void;
  showAddToListOption?: boolean;
  configParams?: ContractTasksConfigParams;
  selectedTasksList?: ContractTaskRow[];
  /**
   * Only required by the add-to-list footer, which renders solely when `showAddToListOption` is set;
   * `InspectionCreation` mounts this grid without it and leaves that flag at its `false` default.
   */
  setSelectedTasksList?: React.Dispatch<React.SetStateAction<ContractTaskRow[]>>;
  /**
   * Passed by `mileStones.tsx` but not read here — selection is always enabled on this grid via
   * `enableSelectAllCheckbox`. Declared so that call site keeps type-checking.
   */
  allowCheckBoxSelection?: boolean;
}

const ContractTasks = forwardRef<ContractTasksGridHandle | null, ContractTasksProps>(
  (
    {
      contractId,
      formId,
      setAttachBtnState,
      showAddToListOption = false,
      configParams = {},
      selectedTasksList = [],
      setSelectedTasksList,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const contractDetails = useSelector((state: RootState) => state.contractDetails);
    const portalData = useSelector((state: RootState) => state.portalData);
    const localRef = useRef<ContractTasksGridHandle | null>(null);
    // `ref` is a `ForwardedRef`, which may also be a callback; every caller passes a ref object, and
    // the assertion is erased at emit so the `||` fallback behaves exactly as before.
    const treeGrid = (ref || localRef) as React.RefObject<ContractTasksGridHandle | null>;
    const [addToListBtnState, setAddToListBtnState] = useState(false);
    const [dataSource, setDataSource] = useState<ContractTaskRow[]>([]);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showTaskGridLoader, setShowTaskGridLoader] = useState(true);

    const isVEPLPortal = portalData?.frontendUrl?.includes("vepl");

    const payload = {
      value: {
        scheduleId: contractDetails.linkedSchedule?._id,
        contractId,
        entity: "tasks",
        contractVersionId: contractDetails.versionId,
        formId,
      },
    };

    const {
      dataSourcePayload = payload,
      dataSourceUrl = contractEntitiesUrl,
      idMapping = "originalTaskId",
      toolbar = null,
    } = configParams;

    const fetchData = async () => {
      // Dynamic dataSourceUrl - endpoint not known at compile time
      const res = await axiosWrapper.post<GetContractLinkedEntitiesResponse>(dataSourceUrl, dataSourcePayload);

      if (res.data.status) {
        setDataSource(() => res.data.result as ContractTaskRow[]);
      }
      setShowTaskGridLoader(false);
    };

    useEffect(() => {
      fetchData();
    }, []);

    useEffect(() => {
      if (treeGrid.current) {
        treeGrid.current.dataset = dataSource;
        if (formId && isVEPLPortal) {
          // setShowDisclaimer(true);
          // const todaysTimeStamp = Date.now();
          // const sevenDayTimeStamp = 604800000;
          // const startDateRange = todaysTimeStamp - sevenDayTimeStamp;
          // const endDateRange = todaysTimeStamp + sevenDayTimeStamp;
          // const startDateFilter = {
          //   field: "startDate",
          //   operator: "greaterthanorequal",
          //   value: startDateRange,
          // };
          // const endDateFilter = {
          //   field: "endDate",
          //   operator: "lessthanorequal",
          //   value: endDateRange,
          // };
          // const filterSettings = [startDateFilter, endDateFilter];
          // treeGrid.current?.setProperties({
          //   filterSettings: { columns: filterSettings },
          // });
        }
      }
    }, [dataSource.length, portalData]);

    const selectionHandlerSlickGrid = () => {
      // Non-null on the ref: the selection handler is wired to the grid itself, so it cannot fire
      // before the grid mounts. Erased at emit — an unattached ref still throws exactly as today.
      const selectedData = treeGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => treeGrid.current!.slickGrid.getDataItem(data));
      if (selectedData.length > 0) {
        let btnState = true;

        selectedData?.map((item) => {
          if (item && (item.isAlreadyAddedInThisForm || item.isAlreadyAddedInThisContract)) {
            btnState = false;
          }
        });
        setAddToListBtnState(btnState);
        if (setAttachBtnState) {
          setAttachBtnState(btnState);
        }
      } else {
        setAddToListBtnState(false);
        if (setAttachBtnState) {
          setAttachBtnState(false);
        }
      }
    };
    const plannedPercentTemplate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => {
      if (!dataContext.plannedStartDate) {
        return "-";
      }
      if (new Date(dataContext.plannedStartDate).getTime() >= new Date().getTime()) {
        return "0 %";
      }
      if (!dataContext.plannedEndDate) {
        return "-";
      }
      if (new Date(dataContext.plannedEndDate).getTime() <= new Date().getTime()) {
        return "100 %";
      }
      const totalPlannedDuration = normalizeNumber(dataContext.plannedEndDate - dataContext.plannedStartDate);
      const currentDuration = Date.now() - dataContext.plannedStartDate;
      const plannedPercentage = normalizeNumber((currentDuration / totalPlannedDuration) * 100);
      return `${plannedPercentage.toFixed(2)} %`;
    };

    const percentCompleteTemplate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => {
      const milisecondsInADay = 86400000;
      const actualDuration = (Date.now() - (dataContext.actualStartDate ?? 0)) / milisecondsInADay;
      const totalDuration = normalizeNumber(
        ((dataContext.endDate ?? 0) - (dataContext.startDate ?? 0)) / milisecondsInADay,
      );

      if (dataContext.actualStartDate && dataContext.actualEndDate) {
        return "100 %";
      }
      if (!dataContext.actualStartDate && !dataContext.actualEndDate) {
        return "0 %";
      }
      if (actualDuration > totalDuration) {
        return `100 %`;
      }
      if (totalDuration > 0) {
        const progress = normalizeNumber((actualDuration / totalDuration) * 100);
        return `${progress.toFixed(2)} %`;
      }
      return "0 %";
    };

    const idFormatter = (
      _row: number,
      _cell: number,
      _value: string | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => getTaskId(dataContext);

    const formatStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => convertDate(Number(dataContext.startDate), "dateForTooltip");

    const formatEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.endDate ? convertDate(Number(dataContext.endDate), "dateForTooltip") : "-");

    const formatPlannedStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.plannedStartDate ? convertDate(Number(dataContext.plannedStartDate), "dateForTooltip") : "-");

    const formatPlannedEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.plannedEndDate ? convertDate(Number(dataContext.plannedEndDate), "dateForTooltip") : "-");

    const formatActualStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.actualStartDate ? convertDate(Number(dataContext.actualStartDate), "dateForTooltip") : "-");

    const formatActualEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.actualEndDate ? convertDate(Number(dataContext.actualEndDate), "dateForTooltip") : "-");

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => convertDate(Number(dataContext.createdDate), "dateForTooltip");

    const columnDefinitions = useMemo(
      () =>
        typedColumns<ContractTaskRow>([
          {
            field: "scheduleCounter",
            id: "scheduleCounter",
            name: t("ID"),
            minWidth: 120,
            formatter: idFormatter,
          },
          {
            field: "title",
            id: "title",
            name: t("Title"),
            filterType: "Text",
            minWidth: 150,
            formatter: Formatters.tree,
          },
          {
            field: "startDate",
            id: "startDate",
            name: t("Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatStartDate,
            _excelValueGetter: (row) => convertDate(Number(row.startDate), "dateForTooltip"),
          },
          {
            field: "endDate",
            id: "endDate",
            name: t("End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatEndDate,
            _excelValueGetter: (row) => (row.endDate ? convertDate(Number(row.endDate), "dateForTooltip") : "-"),
          },
          {
            field: "plannedStartDate",
            id: "plannedStartDate",
            name: t("Planned Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatPlannedStartDate,
            _excelValueGetter: (row) =>
              row.plannedStartDate ? convertDate(Number(row.plannedStartDate), "dateForTooltip") : "-",
          },
          {
            field: "plannedEndDate",
            id: "plannedEndDate",
            name: t("Planned End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatPlannedEndDate,
            _excelValueGetter: (row) =>
              row.plannedEndDate ? convertDate(Number(row.plannedEndDate), "dateForTooltip") : "-",
          },
          {
            field: "actualStartDate",
            id: "actualStartDate",
            name: t("Actual Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatActualStartDate,
            _excelValueGetter: (row) =>
              row.actualStartDate ? convertDate(Number(row.actualStartDate), "dateForTooltip") : "-",
          },
          {
            field: "actualEndDate",
            id: "actualEndDate",
            name: t("Actual End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatActualEndDate,
            _excelValueGetter: (row) =>
              row.actualEndDate ? convertDate(Number(row.actualEndDate), "dateForTooltip") : "-",
          },
          {
            field: "progress",
            id: "plannedProgress",
            name: t("Planned %"),
            formatter: plannedPercentTemplate,
            filterType: "Number",
            minWidth: 120,
            _excelValueGetter: (row) => {
              if (!row.plannedStartDate) return "-";
              if (new Date(row.plannedStartDate).getTime() >= new Date().getTime()) return "0 %";
              if (!row.plannedEndDate) return "-";
              if (new Date(row.plannedEndDate).getTime() <= new Date().getTime()) return "100 %";
              const totalPlannedDuration = normalizeNumber(row.plannedEndDate - row.plannedStartDate);
              const currentDuration = Date.now() - row.plannedStartDate;
              return `${((currentDuration / totalPlannedDuration) * 100).toFixed(2)} %`;
            },
          },
          {
            field: "progress",
            id: "percentComplete",
            name: t("% Complete"),
            formatter: percentCompleteTemplate,
            filterType: "Number",
            minWidth: 120,
            _excelValueGetter: (row) => {
              const milisecondsInADay = 86400000;
              const actualDuration = (Date.now() - (row.actualStartDate ?? 0)) / milisecondsInADay;
              const totalDuration = normalizeNumber(((row.endDate ?? 0) - (row.startDate ?? 0)) / milisecondsInADay);
              if (row.actualStartDate && row.actualEndDate) return "100 %";
              if (!row.actualStartDate && !row.actualEndDate) return "0 %";
              if (actualDuration > totalDuration) return "100 %";
              if (totalDuration > 0) return `${((actualDuration / totalDuration) * 100).toFixed(2)} %`;
              return "0 %";
            },
          },
          {
            field: "createdDate",
            id: "createdDate",
            name: t("Created On"),
            formatter: formatCreatedDate,
            filterType: "Date",
            minWidth: 120,
            _excelValueGetter: (row) => convertDate(Number(row.createdDate), "dateForTooltip"),
          },
        ]),
      [],
    );

    const expandButtonTemplate = () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          onClick={() => treeGrid.current?.treeDataService.toggleTreeDataCollapse(false)}
          id="expand_all"
        >
          <span className="flex items-center gap-1" data-testid="expandAllButton">
            <IconComponent name="Expand" width="1rem" height="1rem" />
            {t("Expand All")}
          </span>
        </ButtonComponent>
      </div>
    );

    const collapseButtonTemplate = () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          onClick={() => treeGrid.current?.treeDataService.toggleTreeDataCollapse(true)}
          id="collapse_all"
        >
          <span className="flex items-center gap-1" data-testid="collapseAllButton">
            <IconComponent name="Collapse" width="1rem" height="1rem" />
            {t("Collapse All")}
          </span>
        </ButtonComponent>
      </div>
    );

    const toolbarOptions = [
      {
        id: "ExpandAll",
        template: expandButtonTemplate,
      },
      {
        id: "CollapseAll",
        template: collapseButtonTemplate,
      },
    ];

    const getTaskGrid = () => (
      <SlickGrid1
        // `ContractTasksGridHandle` above is a deliberate narrow duck-type of SlickGrid1's instance
        // type, so the ref object needs bridging to the generic component's ref. Erased at emit.
        ref={treeGrid as unknown as Parameters<typeof SlickGrid1<ContractTaskRow>>[0]["ref"]}
        treeGrid
        dataset={dataSource}
        idMapping={idMapping}
        hasChildMapping="hasChild"
        parentIdMapping="parentUniqueId"
        enableSelectAllCheckbox
        toolbar={toolbar ? [...toolbar, ...toolbarOptions] : toolbarOptions}
        onSelectedRowIdsChanged={selectionHandlerSlickGrid}
        initialSortColumnId="createdDate"
        enableFiltering={true}
        disableCustomSorting
        gridOptions={{
          enableSorting: true,
        }}
        columnDefinitions={columnDefinitions as unknown as ValidatedColumnDefinition<ContractTaskRow>[]}
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        hideNoDataIllustration
      />
    );
    const getEmptyIllustration = () => <div className="m-6">{t("No Schedule have been linked to this contract")}</div>;

    const handleAddToListTask = () => {
      // Non-null on the ref and on `setSelectedTasksList`: this handler renders inside the
      // add-to-list footer, which only exists when `showAddToListOption` is set, and the callers that
      // set it also supply the setter. Both assertions are erased at emit.
      const selectedTasks = treeGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => treeGrid.current!.slickGrid.getDataItem(data));
      setSelectedTasksList!((prev) => [...prev, ...selectedTasks]);
    };

    const removeTaskClick = (originalTaskId: ContractTaskRow["originalTaskId"]) => {
      setSelectedTasksList!((prev) => prev.filter((task) => task.originalTaskId != originalTaskId));
    };

    const addToList = () => (
      <div className="w-full justify-between h-1/6 items-center flex">
        <div className="w-full h-full flex flex-wrap gap-3 overflow-auto">
          {selectedTasksList.map((task) => (
            <div
              key={task.originalTaskId}
              className="border-2 h-8 max-w-40 gap-2 ps-1 pe-1 border-button_light rounded flex items-center"
            >
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">{getTaskId(task)}</span>
              <span>
                <GrClose size={10} className="cursor-pointer" onClick={() => removeTaskClick(task.originalTaskId)} />
              </span>
            </div>
          ))}
        </div>
        <div>
          <ButtonComponent
            onClick={handleAddToListTask}
            disabled={!addToListBtnState}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          >
            {t("Add To List")}
          </ButtonComponent>
        </div>
      </div>
    );

    const getDisclaimer = () => {
      return (
        <div className="opacity-50 p-2 bg-yellow-200 border rounded-lg flex justify-between items-center">
          <div className="text-sm">
            {t("A Preset filter is being applied , please clear filters to see all activities")}
          </div>
          <div>
            <IconComponent
              onClick={() => setShowDisclaimer(false)}
              className="cursor-pointer items-start"
              name="cross"
              width="1rem"
              height="1rem"
            />
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full gap-5">
        {formId && showDisclaimer && getDisclaimer()}
        {contractDetails.linkedSchedule?._id ? (
          showTaskGridLoader ? (
            <Shimmer />
          ) : (
            getTaskGrid()
          )
        ) : (
          getEmptyIllustration()
        )}
        {showAddToListOption && contractDetails.linkedSchedule?._id && addToList()}
      </div>
    );
  },
);
ContractTasks.displayName = "ContractTasks";
export default ContractTasks;
